import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FirstItem, LastTableItem, TableBodyRow } from './InventoryPage.styles.js';
import { formatDate, priceFormatter } from '../../utils/utils.js';
import MainContainer from '../../components/utils/MainContainer/MainContainer.js';
import TableItem from '../../components/Table/TableItem.js';
import Table from '../../components/Table/Table.js';
import { selectGetMarketplaceItemInventoryPending, selectMarketplaceItemInventory } from '../../store/slices/inventory/slice.js';
import { getMarketplaceItemInventory } from '../../store/slices/inventory/asyncThunks.js';
import { selectedLocale } from '../../store/slices/common/slice.js';

const tableHeader = [
  {
    label: 'product-id',
    sortable: true,
    key: 'productId',
  },
  {
    label: 'sku',
    sortable: true,
    key: 'sku',
  },
  {
    label: 'product-name',
    sortable: true,
    key: 'productName',
  },
  {
    label: 'inventories',
    sortable: true,
    key: 'inventories',
  },
  {
    label: 'buying-price',
    sortable: true,
    key: 'buyingPrice',
  },
  {
    label: 'selling-price',
    sortable: true,
    key: 'sellingPrice',
  },
  {
    label: 'value',
    sortable: true,
    key: 'value',
  },
  {
    label: 'last-re-stock',
    sortable: false,
    key: 'lastReStock',
  },
];

const InventoryPage = () => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);

  const data = useSelector(selectMarketplaceItemInventory);
  const loading = useSelector(selectGetMarketplaceItemInventoryPending);

  const locale = useSelector(selectedLocale);
  const ar = locale === 'ar';

  const perPage = 12;
  const startIndex = (currentPage - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paginatedData = data.slice(startIndex, endIndex);

  useEffect(() => {
    dispatch(getMarketplaceItemInventory())
      .unwrap()
      .then(() => {})
      .catch(error => {});
  }, [dispatch]);

  const totalInventoryValue = data.reduce((acc, product) => {
    if (!product?.inventories || product.inventories.length === 0) return acc;
    const totalStock = product.inventories.reduce((sum, item) => sum + parseInt(item.stock), 0);
    const value = +product?.buying_price * totalStock;
    return acc + value;
  }, 0);

  return (
    <MainContainer pageTitle={'inventory'} exportButton totalInventoryValue={totalInventoryValue}>
      <Table
        headerInformation={tableHeader}
        totalCount={data.length}
        currentPage={currentPage}
        setPage={setCurrentPage}
        perPage={perPage}
        noDataContent={'no-data'}
        lastColumnAligned
        maxReached={false}
        dataLength={data.length}
        dataLoading={loading}
        scroll={true}
        centerText>
        {paginatedData.map((product, key) => {
          return (
            <TableBodyRow key={key} $offClick style={{ height: 'fit-content', minHeight: '64px' }}>
              <TableItem showHover data={product?.id} center style={{ maxHeight: '100%', flex: 1, padding: '10px 0px', paddingLeft: 40 }} />
              <TableItem data={product.sku || '/'} center style={{ maxHeight: '100%', flex: 1, padding: '10px 5px' }} />
              <TableItem data={ar ? product.name_ar : product.name_en || '/'} center style={{ maxHeight: '100%', flex: 1, padding: '10px 0px' }} />
              <TableItem
                style={{ maxHeight: '100%', flex: 1, padding: '10px 0px' }}
                data={
                  product.inventories && product.inventories.length > 0 ? (
                    <div style={{ textAlign: 'center' }}>
                      {product.inventories.map((inventory, index) => {
                        const name = ar ? inventory.name_ar : inventory.name_en;
                        return (
                          <div key={inventory.id || index}>
                            {name} ({inventory.stock})
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    '/'
                  )
                }
                center
              />
              <TableItem
                data={`${priceFormatter(+product?.buying_price)} SR` || '/'}
                center
                style={{ maxHeight: '100%', flex: 1, padding: '10px 0px' }}
              />
              <TableItem
                data={`${priceFormatter(+product?.selling_price)} SR` || '/'}
                center
                style={{ maxHeight: '100%', flex: 1, padding: '10px 0px' }}
              />
              <TableItem
                data={
                  `${priceFormatter(+product?.buying_price * product.inventories.reduce((sum, item) => sum + parseInt(item.stock), 0))} SR` || '/'
                }
                center
                style={{ maxHeight: '100%', flex: 1, padding: '10px 0px' }}
              />
              <LastTableItem data={formatDate(product?.updated_at) || '/'} $offClick style={{ maxHeight: '100%', flex: 1, padding: '10px 0px' }} />
            </TableBodyRow>
          );
        })}
      </Table>
    </MainContainer>
  );
};

export default InventoryPage;
